"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Mail, MessageCircle, Instagram, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function RecursiveFooterMenu({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={`space-y-2.5 ${depth > 0 ? "mt-2 pl-3 border-l border-border/60" : ""}`}>
      {items.map((item, i) => <li key={item.id || i} data-index={i} className="text-sm">
          <Link to={item.href || "#"} className="text-foreground/80 hover:text-primary transition-colors inline-flex items-center gap-1 group font-medium">
            <span>{item.label}</span>
            {depth === 0 && <ArrowUpRight className="size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary" />}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterMenu items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2.5">
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-2.5 text-sm">
        <li data-index={0}>
          <Link to="/ShopPage" className="text-foreground/80 hover:text-primary transition-colors">
            Tienda
          </Link>
        </li>
        <li data-index={1}>
          <Link to="#ritual" className="text-foreground/80 hover:text-primary transition-colors">
            Ritual
          </Link>
        </li>
        <li data-index={2}>
          <Link to="#faq" className="text-foreground/80 hover:text-primary transition-colors">
            Preguntas frecuentes
          </Link>
        </li>
        <li data-index={3}>
          <Link to="#contacto" className="text-foreground/80 hover:text-primary transition-colors">
            Contacto
          </Link>
        </li>
      </ul>;
  }
  return <RecursiveFooterMenu items={menuItems} />;
}
export default function Footer() {
  const prefersReducedMotion = useReducedMotion();
  const currentYear = new Date().getFullYear();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border relative overflow-hidden">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-14 pt-16 md:pt-20 pb-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-14">
          {/* Column 1: Brand & Mission */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-10 w-auto" />
            </Link>
            <p className="text-sm leading-relaxed text-foreground/80 max-w-sm">
              Cosmética y bienestar botánico en México. Formulaciones conscientes a base de hongos funcionales para cuidar rostro, cuerpo y mente en un mismo ritual diario.
            </p>
            <div className="pt-2 flex items-center gap-2 text-xs font-medium text-foreground/70">
              <span className="inline-flex items-center justify-center size-6 rounded-full bg-accent text-accent-foreground">
                <Sparkles className="size-3.5" />
              </span>
              <span>Hecho con tradición micológica en México</span>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div className="lg:col-span-2 space-y-4">
            <p className="font-serif text-base font-medium text-foreground tracking-tight">
              Navegación
            </p>
            <MenuProvider menu_id="23">
              <FooterNavConsumer />
            </MenuProvider>
          </div>

          {/* Column 3: Contact & Support */}
          <div className="lg:col-span-3 space-y-4">
            <p className="font-serif text-base font-medium text-foreground tracking-tight">
              Atención y horarios
            </p>
            <ul className="space-y-3 text-sm text-foreground/80">
              <li data-index={0} className="flex items-start gap-2.5">
                <Clock className="size-4 text-primary shrink-0 mt-0.5" />
                <span>Lunes a domingo: 9:00 - 20:00 CDMX</span>
              </li>
              <li data-index={1} className="flex items-center gap-2.5">
                <Mail className="size-4 text-primary shrink-0" />
                <a href="mailto:contacto@spora.beauty" className="hover:text-primary transition-colors">
                  contacto@spora.beauty
                </a>
              </li>
              <li data-index={2} className="flex items-center gap-2.5">
                <MessageCircle className="size-4 text-primary shrink-0" />
                <Link to="https://wa.me/5639561232" newTab className="hover:text-primary transition-colors inline-flex items-center gap-1">
                  <span>WhatsApp 5639561232</span>
                  <ArrowUpRight className="size-3 text-primary/70" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Community & Social */}
          <div className="lg:col-span-3 space-y-4">
            <p className="font-serif text-base font-medium text-foreground tracking-tight">
              Comunidad
            </p>
            <p className="text-sm text-foreground/80">
              Aprende sobre extractos botánicos, rituales de cuidado consciente y novedades de temporada.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link to="https://www.instagram.com/sporabeauty/" newTab className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-secondary text-secondary-foreground text-xs font-medium hover:bg-accent hover:text-accent-foreground transition-colors border border-border">
                <Instagram className="size-3.5" />
                <span>Instagram</span>
              </Link>
              <Link to="https://tiktok.com" newTab className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-secondary text-secondary-foreground text-xs font-medium hover:bg-accent hover:text-accent-foreground transition-colors border border-border">
                <span className="font-semibold text-xs tracking-tighter">TT</span>
                <span>TikTok</span>
              </Link>
            </div>
          </div>
        </motion.div>

        <Separator className="bg-border" />

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-foreground/70">
          <div className="flex items-center gap-3">
            <span>
              {`© ${currentYear} Spora. Todos los derechos reservados.`}
            </span>
            <span className="inline-block size-1 rounded-full bg-border" />
            <span className="font-medium text-foreground/80">MXN ($)</span>
          </div>

          <div className="flex items-center gap-6">
            <Link to="#" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
              Términos y condiciones
            </Link>
            <Link to="#" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
              Aviso de privacidad
            </Link>
            <Link to="#" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
              Política de devoluciones
            </Link>
          </div>
        </div>
      </div>
    </section>;
}